import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI } from '../api';
import { X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import BrandsCarousel from '../components/BrandsCarousel';
import { Reveal } from '../components/Reveal';

const EASE = [0.16, 1, 0.3, 1];

const AllProjectsPage = () => {
  const { t, language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await projectsAPI.getAll();
        setProjects(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading projects:', error);
        setLoading(false);
      }
    };
    loadProjects();
  }, []);

  // Obtener categorías únicas
  const categories = ['all', ...new Set(projects.map(p => p.category))];

  // Filtrar proyectos
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  // Funciones de navegación de proyectos
  const openProject = (project, index) => {
    setSelectedProject(project);
    setSelectedIndex(index);
    setCurrentImageIndex(0);
  };

  // Funciones de navegación de imágenes dentro del proyecto
  const goToPreviousImage = () => {
    const images = selectedProject?.images || [selectedProject?.image];
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
    setCurrentImageIndex(newIndex);
  };

  const goToNextImage = () => {
    const images = selectedProject?.images || [selectedProject?.image];
    const newIndex = currentImageIndex < images.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
  };

  // Navegación con teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedProject) return;
      if (e.key === 'ArrowLeft') goToPreviousImage();
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'Escape') setSelectedProject(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject, currentImageIndex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center pt-20">
        <p className="text-white/40">{language === 'es' ? 'Cargando proyectos...' : 'Loading projects...'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Hero Section */}
      <section className="relative h-[62vh] min-h-[480px] flex items-center overflow-hidden noise-overlay">
        <div className="absolute inset-0">
          <motion.img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=85"
            alt="Proyectos"
            className="w-full h-full object-cover"
            initial={{ scale: 1.12, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.4 }}
            transition={{ duration: 1.5, ease: EASE }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
        </div>
        <div className="absolute -top-16 right-0 w-[34rem] h-[34rem] bg-brand/15 rounded-full blur-[140px]" />

        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.p className="overline mb-5" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}>
            {t('nav.proyectos')}
          </motion.p>
          <motion.h1
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-extralight text-white tracking-tight leading-[0.95] max-w-3xl"
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          >
            {t('projects.pageTitle')}
          </motion.h1>
          <motion.p className="mt-6 text-lg text-white/60 max-w-2xl leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}>
            {t('projects.pageSubtitle')}
          </motion.p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-10 border-b border-white/10 bg-obsidian">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`filter-${cat}`}
                className={`px-6 py-2.5 rounded-sm text-sm uppercase tracking-wider transition-all ${
                  filter === cat
                    ? 'bg-brand text-white'
                    : 'border border-white/15 text-white/50 hover:border-brand hover:text-brand-light'
                }`}
              >
                {cat === 'all' ? t('projects.all') : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 md:py-24 bg-obsidian">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="group relative overflow-hidden rounded-2xl cursor-pointer"
                onClick={() => openProject(project, index)}
                data-testid={`project-card-${project.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, ease: EASE, delay: (index % 3) * 0.08 }}
              >
                <div className="relative h-[26rem] bg-surface-hi overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />

                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <p className="overline mb-2">{project.category}</p>
                    <h3 className="font-heading text-2xl font-light text-white mb-2 tracking-tight">{project.title}</h3>
                    <p className="text-white/60 text-sm max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 overflow-hidden transition-all duration-500">
                      {project.subtitle || project.description?.substring(0, 100)}
                    </p>
                  </div>

                  <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-brand/40 rounded-2xl transition-all duration-500" />
                </div>
              </motion.div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-white/40 text-lg">{t('projects.noProjects')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-obsidian/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedProject(null)}
        >
          <div 
            className="bg-surface border border-white/10 rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto my-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image Gallery */}
            <div className="relative">
              {/* Main Image */}
              <img
                src={(selectedProject.images && selectedProject.images[currentImageIndex]) || selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-72 md:h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
              
              {/* Close Button */}
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              >
                <X size={24} />
              </button>

              {/* Image Navigation Arrows */}
              {selectedProject.images && selectedProject.images.length > 1 && (
                <>
                  <button
                    onClick={goToPreviousImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-blue-600 text-white p-3 rounded-full transition-all"
                  >
                    <ChevronLeft size={28} />
                  </button>
                  <button
                    onClick={goToNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-blue-600 text-white p-3 rounded-full transition-all"
                  >
                    <ChevronRight size={28} />
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                    {currentImageIndex + 1} / {selectedProject.images.length}
                  </div>
                </>
              )}

              {/* Thumbnail Dots */}
              {selectedProject.images && selectedProject.images.length > 1 && (
                <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
                  {selectedProject.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex ? 'bg-blue-500 w-6' : 'bg-white/50 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="p-8 md:p-10">
              <p className="overline mb-2">{selectedProject.category}</p>
              <h2 className="font-heading text-3xl font-light text-white mb-4 tracking-tight">
                {selectedProject.title}
              </h2>
              <p className="text-white/60 text-lg mb-6">
                {selectedProject.subtitle}
              </p>
              
              {/* Full Description */}
              <div className="mb-8">
                <h3 className="text-white/80 text-xs uppercase tracking-widest mb-3">{t('projects.projectDescription')}</h3>
                <p className="text-white/55 leading-relaxed">
                  {selectedProject.description}
                </p>
              </div>

              {/* Brands Used */}
              {selectedProject.brands && selectedProject.brands.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-white/80 text-xs uppercase tracking-widest mb-3">{t('projects.brandsUsed')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.brands.map((brand, idx) => (
                      <span 
                        key={idx}
                        className="bg-surface-hi text-white/70 px-4 py-2 rounded-sm text-sm border border-white/10"
                      >
                        {brand}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-4 pt-6 border-t border-white/10">
                <Link
                  to="/contacto"
                  className="btn-primary px-6 py-3 rounded-sm text-sm uppercase tracking-wider font-medium"
                  onClick={() => setSelectedProject(null)}
                >
                  {t('projects.requestInfo')}
                </Link>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="btn-ghost px-6 py-3 rounded-sm text-sm uppercase tracking-wider font-medium"
                >
                  {t('projects.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brands / Partners */}
      <BrandsCarousel background="bg-surface" />

      {/* CTA Section */}
      <section className="py-24 bg-obsidian border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal className="glass-card p-12 md:p-16 rounded-3xl text-center max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6 tracking-tight">
              {t('cta.haveProject')}
            </h2>
            <p className="text-white/55 text-lg mb-10 max-w-2xl mx-auto">
              {t('cta.discoverHow')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto" className="btn-primary px-10 py-4 rounded-sm text-sm uppercase tracking-wider font-medium">
                {t('cta.requestConsultation')}
              </Link>
              <Link to="/servicios" className="btn-ghost px-10 py-4 rounded-sm text-sm uppercase tracking-wider font-medium">
                {t('nav.servicios')}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AllProjectsPage;
